// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { SxProps, Theme } from '@mui/material/styles';
import CHATBOT_DRAWER_WIDTH from './constants';

const styles = (theme: Theme): Record<string, SxProps<Theme>> => ({
  launcher: {
    position: 'fixed',
    // Raised so it sits just above the viewer's "Show panos" button (bottom-right) without overlap.
    bottom: theme.spacing(11),
    right: theme.spacing(3),
    // Above fullscreen MUI dialogs (e.g. the Create Order form) so the assistant stays reachable.
    zIndex: theme.zIndex.modal + 1,
    // Extended pill: the round Midnight brand icon + the product name, on the icon's own Midnight
    // so they read as one mark. Bright Blue on hover (brand primary) keeps the affordance obvious.
    backgroundColor: '#153B5A',
    color: '#FFFFFF',
    textTransform: 'none',
    fontWeight: 600,
    letterSpacing: '0.02em',
    paddingRight: theme.spacing(2),
    '&:hover': { backgroundColor: '#01A1DD' },
  },
  // The round icon inside the extended launcher pill, beside the "Khora" label.
  launcherIcon: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    marginRight: theme.spacing(1),
  },
  drawerPaper: {
    width: CHATBOT_DRAWER_WIDTH,
    maxWidth: '100vw',
    display: 'flex',
    flexDirection: 'column',
    // Keep the panel on top of fullscreen dialogs (Create Order, etc.).
    zIndex: theme.zIndex.modal + 1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 600,
  },
  // Groups the download + close buttons together on the right of the header (so download sits next
  // to the X, not floated to the middle by the header's space-between).
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  closeButton: {
    color: theme.palette.primary.contrastText,
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  empty: {
    color: theme.palette.text.secondary,
    textAlign: 'center',
    marginTop: theme.spacing(4),
  },
  // "Session may be out of date" banner — shown when the gdb was updated since this chat was saved.
  staleChip: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderRadius: 1,
    backgroundColor: 'rgba(255, 148, 36, 0.14)', // brand Bright Orange #FF9424 tint (advisory)
    border: '1px solid rgba(255, 148, 36, 0.4)',
  },
  staleChipText: {
    flexGrow: 1,
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    lineHeight: 1.3,
  },
  staleChipClose: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.25),
  },
  bubble: {
    padding: theme.spacing(1, 1.5),
    borderRadius: 2,
    maxWidth: '85%',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  userBubble: {
    alignSelf: 'flex-end',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  assistantBubble: {
    alignSelf: 'flex-start',
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
  spinner: {
    alignSelf: 'flex-start',
    margin: theme.spacing(1),
  },
  // "Khora is thinking…" — a live status line with three bouncing dots while an agent turn runs.
  thinking: {
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    margin: theme.spacing(0.5, 1),
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 2,
    backgroundColor: theme.palette.action.hover,
  },
  thinkingDots: {
    display: 'flex',
    gap: '3px',
    '@keyframes khoraBounce': {
      '0%, 80%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
      '40%': { transform: 'translateY(-4px)', opacity: 1 },
    },
  },
  thinkingDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    animation: 'khoraBounce 1.4s infinite ease-in-out both',
    '&:nth-of-type(2)': { animationDelay: '0.2s' },
    '&:nth-of-type(3)': { animationDelay: '0.4s' },
  },
  thinkingText: {
    fontSize: '0.8rem',
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
    padding: theme.spacing(0, 2),
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  // An image attached to a message (the screenshot thumbnail in the bubble).
  messageImage: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: 200,
    borderRadius: 1,
    marginBottom: theme.spacing(0.5),
  },
  // A chart in an assistant bubble + its "Pin to dashboard" action.
  chartBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginTop: theme.spacing(0.5),
  },
  // Small "download this response" icon at the bottom of an assistant bubble.
  messageDownload: {
    alignSelf: 'flex-end',
    padding: theme.spacing(0.25),
    marginTop: theme.spacing(0.25),
    color: theme.palette.text.secondary,
  },
  // A Khora-produced downloadable file (report/CSV/GeoJSON) link in an assistant bubble.
  fileDownload: {
    alignSelf: 'flex-start',
    textTransform: 'none',
    marginTop: theme.spacing(0.5),
    maxWidth: '100%',
    justifyContent: 'flex-start',
  },
  // An interactive make_chart chart in an assistant bubble (recharts + data-table/derivation popups).
  chartCard: {
    width: '100%',
    marginTop: theme.spacing(0.75),
    padding: theme.spacing(0.75),
    borderRadius: 1,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  chartHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5),
  },
  chartTitle: {
    fontWeight: 600,
    fontSize: '0.78rem',
    color: theme.palette.text.primary,
  },
  chartTools: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    flexShrink: 0,
  },
  chartToolBtn: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.25),
    '&:hover': { color: theme.palette.primary.main },
  },
  chartContainer: {
    width: '100%',
    // Clicking a chart element (esp. a pie sector) draws a rectangular SVG focus outline; suppress it
    // so the selection reads as the slice/bar, not a box.
    '& :focus, & :focus-visible': { outline: 'none' },
  },
  chartHint: {
    fontSize: '0.66rem',
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(0.5),
  },
  // "How this chart is derived" explanation, shown under the chart when the info button is on.
  methodPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    margin: theme.spacing(0.5, 0, 0),
    padding: theme.spacing(0.75, 1),
    borderRadius: 1,
    backgroundColor: 'rgba(1, 161, 221, 0.10)', // AirWorks blue tint
    borderLeft: '2px solid rgba(1, 161, 221, 0.6)',
  },
  methodLine: {
    color: theme.palette.text.secondary,
    fontSize: '0.72rem',
    lineHeight: 1.35,
  },
  // Data-table popup: scrolls horizontally inside the card instead of overflowing the panel.
  tableWrap: {
    overflowX: 'auto',
    maxWidth: '100%',
    maxHeight: 220,
    marginTop: theme.spacing(0.5),
  },
  thCell: {
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    fontSize: '0.7rem',
    fontWeight: 600,
  },
  tdCell: {
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    fontSize: '0.7rem',
  },
});

export default styles;
